import React from 'react'
import { connect } from 'react-redux'
import styles from './Home.module.css'
import orderBy from 'lodash/orderBy'
import {Header} from './Header'
import Spinner from './Spinner'
import add from '../images/add.svg'
import { Button, Input, FormGroup, Label } from 'reactstrap'
import { Dialog, DialogContent, DialogFooter, DialogHeader } from './Dialog'
import logo from '../images/wistor-mark-color.svg'
// @ts-expect-error
import { Helmet } from 'react-helmet'
import Footer from './Footer'
import { fetchProjects, createProject } from '../redux/projects'
import { useHistory } from 'react-router-dom'
import { getAuthHeaders, useWistorAuth } from '../api/auth'
import { useSelector } from '../store'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import axios from 'axios'
import { GroupedSearchOptions, SearchDropdown, SearchOptionGroup } from './ui-primitives/search-dropdown'

type State = {
  dialogVisible: boolean,
  values: Partial<Record<'title' | 'path', string>>
}

function Home(props: {
  fetchProjects: typeof fetchProjects
  createProject: typeof createProject
}) {
  const [state, setState] = React.useState<State>({dialogVisible: false, values: {} })
  const store = useSelector(s => ({projects: s.projects}))
  const auth = useWistorAuth()
  const history = useHistory()

  const gotoLogin = () => history.push('/login', '')

  React.useEffect(() => {
    if (auth.status !== 'authorized') return gotoLogin()
    if (auth.user.role !== 'superadmin' && auth.user.project) {
      return history.replace(`/projects/${auth.user.project.path}`)
    }
    props.fetchProjects((error: any) => {
      if (error.response.status === 401) {
        history.push('/login', 'projects')
      }
    })
  }, [])

  const onDialogOkClick = () => {
    if (auth.status !== 'authorized') return gotoLogin()
    props.createProject(state.values, (error: any) => {
      if (error.response.status === 401) history.push('/login', 'projects')

    })
    setState(s => ({...s, dialogVisible: false }))
  }

  if (store.projects.loading) return <Spinner />
  if (!store.projects.loaded) return null

  let projects = store.projects.items
  // projects = orderBy(projects, ['createdAt'], ['asc']);
  // console.log(projects, role)
  projects = orderBy(projects, ['title'], ['asc'])
  return (
    <div>
      <Helmet>
        <title>Wistor</title>
        <link rel="icon" type="image/png" href="/favicon.png" sizes="16x16" />
      </Helmet>
      <Header />
      <div className={styles.mainContainer}>

        <div className={styles.main}>
          <div className={styles.content}>
            <img
              src={logo}
              alt="logo"
              style={{ maxHeight: '96px', margin: '0 12px 12px' }}
            />
            <div className={styles.title}>Welcome to Wistor</div>
            <p className={styles.paragr}>
                Data-centric working made easy
            </p>
            <div className="px-2 mb-2"><SearchAndGoToApp /></div>
            <div className={styles.subTitle}>Select a project:</div>
            <div className={styles.projects}>
              {projects.map((project: any) => {
                return (
                  <div
                    className={styles.project}
                    key={project.id}
                    onClick={() =>
                      history.push(`/projects/${project.path}`)
                    }
                  >
                    <div>{project.title}</div>
                  </div>
                )
              })}
              {auth.status === 'authorized' && auth.user?.role === 'superadmin' && (
                <div
                  className={styles.project}
                  onClick={() => setState(s => ({...s, dialogVisible: true }))}
                >
                  <div>
                    <img className="inline" src={add} alt="add" style={{ width: '24px' }} />
                    <span
                      style={{ verticalAlign: 'middle', paddingLeft: '12px' }}
                    >
                      Create a new project...
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {state.dialogVisible && (
        <Dialog onClose={() => setState(s => ({ ...s, dialogVisible: false }))}>
          <DialogHeader title="Add project" />
          <DialogContent>
            <FormGroup>
              <Label>Title</Label>
              <Input
                name="title"
                className="dark-form-control"
                value={state.values.title || ''}
                type="text"
                onChange={(e) => setState(s => ({ ...s, values: { ...s.values, title: e.target.value } }))}
              />
            </FormGroup>
            <FormGroup>
              <Label>Path</Label>
              <Input
                name="path"
                className="dark-form-control"
                value={state.values.path || ''}
                type="text"
                onChange={(e) => setState(s => ({ ...s, values: { ...s.values, path: e.target.value } }))}
              />
            </FormGroup>
          </DialogContent>
          <DialogFooter>
            <Button
              color="secondary"
              style={{ minWidth: '120px' }}
              onClick={() => setState(s => ({...s, dialogVisible: false }))}
              aria-label="Cancel"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              style={{ minWidth: '120px' }}
              onClick={onDialogOkClick}
              aria-label="OK"
            >
              OK
            </Button>
          </DialogFooter>
        </Dialog>
        )}
    </div>
)
}

export default connect(undefined, {
  fetchProjects, createProject
})(Home as any)

const UNKNOWN_LIST_SCHEMA = z.array(z.unknown())

const APP_SCHEMA = z.object({
  id: z.literal(null),
  path: z.string(),
  title: z.string(),
  project: z.object({
    id: z.literal(null),
    path: z.string(),
    title: z.string(),
  }),
})

function SearchAndGoToApp(props: {
}): React.ReactNode {
  const [search, setSearch] = React.useState<string>('')
  const history = useHistory()

  const authHeaders = getAuthHeaders()
  const appsResult = useQuery<GroupedSearchOptions>({
    retry: 1,
    retryDelay: 0,
    enabled: Boolean(authHeaders.Authorization),
    queryKey: ["fetch-import-from-platform", authHeaders.Authorization],
    queryFn: async () => {
      const res = await axios.get('/api/list-viewers', {headers: authHeaders})
      const someList = UNKNOWN_LIST_SCHEMA.parse(res.data)
      const apps: z.infer<typeof APP_SCHEMA>[] = []
      for (const maybeApp of someList) {
        const parsed = APP_SCHEMA.safeParse(maybeApp)
        if (parsed.success) {
          apps.push(parsed.data)
        } else {
          console.error(parsed.error)
        }
      }
      return groupApps(apps)
    }
  })

  return (
    <SearchDropdown
      labelConfig={{placeholder: 'Enter keywords to find app to go to...'}}
      highlightWords
      syncSearchWithLabel
      variant="big"
      search={search}
      onSearch={setSearch}
      isLoading={appsResult.isLoading}
      options={appsResult.data ?? []}
      onOpenChange={() => appsResult.refetch()}
      value={null}
      filterFn={scoreMatch}
      onChange={(url) => {
        if (!url) return
        history.push(url)
      }}
    />
  )
}

function groupApps(apps: z.infer<typeof APP_SCHEMA>[]) {
  const groupedApps = new Map<string, SearchOptionGroup>()
  for (const app of apps) {
    const projectPath = app.project.path
    if (!groupedApps.has(projectPath)) {
      groupedApps.set(projectPath, {
        label: app.project.title,
        options: []
      })
    }
    const options = groupedApps.get(projectPath)!.options
    options.push({
      label: `${app.project.title}: ${app.title}`,
      value: `/projects/${app.project.path}/${app.path}`
    })
  }
  return groupedApps
}

/**
 * Intended priority:
 * - exact value match (case-sensitive)
 * - exact label match (case-insensitive)
 * - then any keyword match (case-insensitive)
 */
function scoreMatch(value: string, search: string, keywords: string[] = []): number {
    const label = keywords[0]
    if (value === search) return 1
    if (label && label.toLowerCase() === search.toLowerCase()) return 0.9
    if (keywords.join(' ').toLowerCase().includes(search.toLowerCase())) return 0.5
    return 0
}
